import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';

const Login = ({ onLoginSuccess }) => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(''); // Estado para mensajes de éxito
  const [isRegister, setIsRegister] = useState(false);
  const [activeView, setActiveView] = useState(null); // 'about', 'contact', 'faq', 'planes'
  const [menuOpen, setMenuOpen] = useState(false);
  
  // Nuevos estados para el flujo dinámico de verificación OTP
  const [isNotVerified, setIsNotVerified] = useState(false);
  const [emailToVerify, setEmailToVerify] = useState('');
  const [otpToken, setOtpToken] = useState('');

  // Estado para controlar el Spinner e impedir el doble clic
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Andromeda - Auth & Solutions";
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true); // Activamos el spinner de carga inmediatamente

    try {
      if (isRegister) {
        // Validaciones frontend
        if (password !== confirmPassword) {
          setError('Las contraseñas no coinciden.');
          setLoading(false); // Detenemos la carga si falla la validación visual
          return;
        }
        if (!acceptedTerms) {
          alert('Debes aceptar los Términos de Servicio y la Política de Privacidad.');
          setLoading(false); // Detenemos la carga si falla la validación visual
          return;
        }

        await authService.register({ username, email, password, confirmPassword, acceptedTerms }); 
        setSuccess('¡Registro exitoso! Por favor, introduce el código de verificación que enviamos a tu correo.');
        setEmailToVerify(email);
        setIsNotVerified(true); // Saltamos directo al modo OTP tras registrar
        setIsRegister(false);
        setUsername('');
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setAcceptedTerms(false);
      } else {
        // Login normal
        const response = await authService.login({ username, password });
        const token = response.data?.token || response.token;

        if (token) {
          sessionStorage.setItem('token', token);
          onLoginSuccess();
          navigate('/dashboard');
        }
      }
    } catch (error) {
      console.error('Registration/Login Error:', error);
      const response = error.response;

      // CAPTURAMOS EL ESCENARIO DE CORREO NO VERIFICADO (HTTP 403)
      if (response && response.status === 403 && response.data?.notVerified) {
        setIsNotVerified(true);
        setEmailToVerify(response.data.email || username);
        setSuccess('Tu cuenta requiere verificación. Por favor introduce el código enviado a tu correo.');
      } else {
        const errorMsg = response?.data?.message || response?.data?.error || 'Error en las credenciales o datos duplicados.';
        setError(errorMsg);
      }
    } finally {
      // El bloque finally se ejecuta SIEMPRE (tanto si la petición fue exitosa como si falló)
      setLoading(false);
    }
  };

  // Manejador exclusivo para enviar el token OTP a la API con soporte de carga
  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await authService.verifyEmail(emailToVerify, otpToken);
      if (response.data?.success || response.success) {
        setSuccess('¡Correo electrónico verificado con éxito! Ya puedes iniciar sesión.');
        setIsNotVerified(false); // Regresa al login tradicional
        setOtpToken('');
      }
    } catch (otpError) {
      console.error('OTP Verification Error:', otpError);
      setError(otpError.response?.data?.message || 'El código de verificación es incorrecto o ha expirado.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleMode = () => {
    setIsRegister(!isRegister);
    setIsNotVerified(false);
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setAcceptedTerms(false);
    setError('');
    setSuccess('');
  };

  const SecondaryView = ({ title, children }) => (
    <div style={{ padding: '40px 20px', fontFamily: 'sans-serif', backgroundColor: '#e6f0e9', minHeight: '100vh', textAlign: 'center' }}>
      <h1 style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#007A33', marginBottom: '10px' }}>
        {title}
      </h1>
      <div style={{ maxWidth: '1000px', margin: '0 auto 40px auto' }}>
        {children}
      </div>
      <button 
        onClick={() => setActiveView(null)} 
        style={{ padding: '12px 30px', fontSize: '1.1em', backgroundColor: '#007A33', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}
      >
        Volver al Inicio de Sesión
      </button>
    </div>
  );

  if (activeView === 'planes') {
    return (
      <SecondaryView title="Planes y Suscripciones Andromeda">
        <p style={{ fontSize: '1.2rem', color: '#6c757d', marginBottom: '30px' }}>
          Elige la escala ideal para el procesamiento automatizado de tus facturas.
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', flexWrap: 'wrap' }}>
          <div style={{ border: '1px solid #ccc', borderRadius: '10px', padding: '20px', width: '280px', backgroundColor: '#f8f9fa', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
            <h3>Trial</h3>
            <p style={{ fontSize: '1.8rem', fontWeight: 'bold', margin: '10px 0', color: '#333' }}>$0 <span style={{ fontSize: '1rem', color: '#777' }}>/ mes</span></p>
            <ul style={{ textAlign: 'left', paddingLeft: '20px', lineHeight: '1.8em', color: '#555' }}>
              <li><strong>15 Facturas</strong> al mes</li>
              <li>1 Usuario activo</li>
              <li>Almacenamiento temporal</li>
              <li>Soporte vía FAQ</li>
            </ul>
          </div>
          <div style={{ border: '2px solid #007A33', borderRadius: '10px', padding: '20px', width: '280px', backgroundColor: '#fff', boxShadow: '0 6px 12px rgba(0,122,51,0.15)', position: 'relative' }}>
            <span style={{ position: 'absolute', top: '-12px', right: '20px', backgroundColor: '#007A33', color: 'white', padding: '2px 10px', borderRadius: '10px', fontSize: '0.8rem', fontWeight: 'bold' }}>POPULAR</span>
            <h3 style={{ color: '#007A33' }}>Personal</h3>
            <p style={{ fontSize: '1.8rem', fontWeight: 'bold', margin: '10px 0', color: '#333' }}>$19 <span style={{ fontSize: '1rem', color: '#777' }}>/ mes</span></p>
            <ul style={{ textAlign: 'left', paddingLeft: '20px', lineHeight: '1.8em', color: '#555' }}>
              <li><strong>200 Facturas</strong> al mes</li>
              <li>1 Usuario independiente</li>
              <li>Almacenamiento persistente</li>
              <li>Descarga de reportes Excel/PDF</li>
              <li>Soporte estándar por Email</li>
            </ul>
          </div>
          <div style={{ border: '1px solid #ccc', borderRadius: '10px', padding: '20px', width: '280px', backgroundColor: '#f8f9fa', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
            <h3 style={{ color: '#111' }}>Enterprise</h3>
            <p style={{ fontSize: '1.8rem', fontWeight: 'bold', margin: '10px 0', color: '#333' }}>$99 <span style={{ fontSize: '1rem', color: '#777' }}>/ mes</span></p>
            <ul style={{ textAlign: 'left', paddingLeft: '20px', lineHeight: '1.8em', color: '#555' }}>
              <li><strong>5000 Facturas</strong> al mes</li>
              <li><strong>Multi-tenant</strong> (Hasta 10 usuarios)</li>
              <li>Espacio compartido de directorios</li>
              <li>Módulo de analítica avanzado</li>
              <li>Soporte prioritario 24/7</li>
            </ul>
          </div>
        </div>
      </SecondaryView>
    );
  }

  if (activeView === 'about') {
    return (
      <SecondaryView title="Quiénes Somos">
        <div style={{ padding: '30px', backgroundColor: 'white', borderRadius: '8px', borderLeft: '8px solid #007A33', textAlign: 'left', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }}>
          <p style={{ fontSize: '1.2em', lineHeight: '1.8', color: '#333', margin: 0 }}>
            <strong>Andromeda</strong> es una solución tecnológica de vanguardia diseñada para transformar y simplificar la gestión financiera personal y empresarial. Nacimos con el objetivo de eliminar la tediosa transcripción manual de datos, utilizando algoritmos avanzados de Inteligencia Artificial para procesar, extraer y organizar la información de facturas y recibos de manera automática.
          </p>
        </div>
      </SecondaryView>
    );
  }

  if (activeView === 'contact') {
    return (
      <SecondaryView title="Contáctenos">
        <div style={{ padding: '30px', backgroundColor: 'white', borderRadius: '8px', borderLeft: '8px solid #007A33', textAlign: 'left', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }}>
          <h3 style={{ color: '#007A33', marginBottom: '15px' }}>¿Tienes alguna duda, sugerencia o requieres soporte técnico con la plataforma?</h3>
          <p style={{ fontSize: '1.1em', color: '#333' }}>
            <strong>Correo Electrónico:</strong> <span style={{ color: '#007A33' }}>contacto@andromedasolution.com</span>
          </p>
        </div>
      </SecondaryView>
    );
  }

  if (activeView === 'faq') {
    const faqData = [
      { q: "¿Qué es Andromeda?", a: "Andromeda es una plataforma inteligente diseñada para automatizar la gestión de tus facturas utilizando Inteligencia Artificial." },
      { q: "¿La plataforma acepta facturas de cualquier país?", a: "Actualmente está optimizada para las regulaciones y estructuras de documentos fiscales venezolanos." }
    ];

    return (
      <SecondaryView title="FAQ: Preguntas Frecuentes - Andromeda">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', textAlign: 'left' }}>
          {faqData.map((item, index) => (
            <div key={index} style={{ padding: '20px', backgroundColor: 'white', borderRadius: '8px', borderLeft: '5px solid #007A33', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
              <h3 style={{ color: '#007A33', marginBottom: '10px', fontSize: '1.2rem' }}>{item.q}</h3>
              <p style={{ color: '#555', lineHeight: '1.6', margin: 0 }}>{item.a}</p>
            </div>
          ))}
        </div>
      </SecondaryView>
    );
  }

  return (
    <div className="login-page" style={{ padding: '20px', fontFamily: 'sans-serif', backgroundColor: '#e6f0e9', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <style>{`
        .login-header { display: flex; justify-content: flex-end; max-width: 1200px; margin: 0 auto; width: 100%; }
        .login-logo { width: 100px; height: auto; }
        .login-main { display: flex; flex: 1; align-items: center; justify-content: center; max-width: 1200px; margin: 0 auto; width: 100%; position: relative; }
        .login-nav { position: absolute; left: 0; display: flex; flex-direction: column; gap: 12px; text-align: left; z-index: 10; }
        .hamburger { display: none; cursor: pointer; flex-direction: column; gap: 5px; position: absolute; left: 0; top: 0; z-index: 20; }
        .hamburger div { width: 25px; height: 3px; background-color: #007A33; border-radius: 2px; }
        
        /* ESTILOS DEL SPINNER VISUAL */
        .spinner {
          border: 3px solid rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          border-top: 3px solid #fff;
          width: 18px;
          height: 18px;
          animation: spin 0.8s linear infinite;
          display: inline-block;
          margin-right: 8px;
          vertical-align: middle;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Comportamiento visual de botones bloqueados */
        button:disabled {
          background-color: #52a472 !important;
          cursor: not-allowed !important;
          opacity: 0.8;
        }

        @media (max-width: 768px) {
          .login-header { justify-content: center; }
          .login-nav { display: ${menuOpen ? 'flex' : 'none'}; position: fixed; top: 0; left: 0; width: 250px; height: 100vh; background: white; padding: 60px 20px; box-shadow: 2px 0 10px rgba(0,0,0,0.1); }
          .hamburger { display: ${menuOpen ? 'none' : 'flex'}; top: -10px; }
          .login-main { flex-direction: column; padding-top: 40px; }
        }
      `}</style>

      <div className="login-header">
        <img src="/Logo-Andromeda.png" className="login-logo" alt="Andromeda Logo" />
      </div>

      <div className="login-main">
        <div className="hamburger" onClick={() => setMenuOpen(!menuOpen)}>
          <div /><div /><div />
        </div>

        <div className="login-nav">
          {menuOpen && <button onClick={() => setMenuOpen(false)} style={{ position: 'absolute', top: '10px', right: '10px', background: 'none', border: 'none', fontSize: '1.5em', color: '#007A33', cursor: 'pointer' }}>✕</button>}
          {[
            { label: '1) Quienes somos', view: 'about' },
            { label: '2) Contactenos', view: 'contact' },
            { label: '3) FAQ', view: 'faq' },
            { label: '4) Planes Y subcripciones', view: 'planes' }
          ].map((item) => (
            <button key={item.view} onClick={() => { setActiveView(item.view); setMenuOpen(false); }} style={{ background: 'none', border: 'none', color: '#007A33', fontWeight: 'bold', cursor: 'pointer', fontSize: '1em', textDecoration: 'underline', padding: '5px 0', textAlign: 'left' }}>
              {item.label}
            </button>
          ))}
        </div>

        <div style={{ textAlign: 'center', maxWidth: '500px', zIndex: 5 }}>
          <h1 style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#007A33', margin: '5px 0' }}>
            Andromeda-Auth V1.0
          </h1>
          <p style={{ fontSize: '1.1em', color: '#007A33', fontWeight: 'bold', margin: '0 auto 20px auto' }}>
            Procesa tus facturas con IA y guarda los datos a la nube, mantén el control de tus gastos
          </p>
          
          <div style={{ padding: '20px 25px', border: '1px solid #e0e0e0', borderRadius: '8px', boxShadow: '0 4px 10px rgba(0,0,0,0.05)', backgroundColor: '#fff' }}>
            
            <h2 style={{ marginBottom: '15px', marginTop: '0', color: '#333', fontSize: '1.5rem' }}>
              {isNotVerified ? 'Verificación de Seguridad' : isRegister ? 'Crear Cuenta' : 'Iniciar Sesión'}
            </h2>
            
            {error && (
              <div style={{ backgroundColor: '#fff1f0', border: '1px solid #ffa39e', color: '#cf1322', padding: '10px', borderRadius: '5px', marginBottom: '15px', fontSize: '0.9em' }}>
                {error}
              </div>
            )}

            {success && (
              <div style={{ backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', color: '#389e0d', padding: '10px', borderRadius: '5px', marginBottom: '15px', fontSize: '0.9em' }}>
                {success}
              </div>
            )}

            {/* RENDERIZADO CONDICIONAL DE FORMULARIOS */}
            {!isNotVerified ? (
              // FORMULARIO TRADICIONAL DE LOGIN / REGISTRO
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <input 
                  type="text" 
                  placeholder="Nombre de Usuario o Correo" 
                  value={username} 
                  onChange={(e) => setUsername(e.target.value)} 
                  style={{ padding: '10px', fontSize: '1em', borderRadius: '5px', border: '1px solid #007A33' }} 
                  disabled={loading}
                  required
                />
                {isRegister && (
                  <input 
                    type="email" 
                    placeholder="Correo Electrónico" 
                    value={email} 
                    onChange={(e) => setEmail(e.target.value)} 
                    style={{ padding: '10px', fontSize: '1em', borderRadius: '5px', border: '1px solid #007A33' }} 
                    disabled={loading}
                    required
                  />
                )}
                <input 
                  type="password" 
                  placeholder="Contraseña" 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)} 
                  style={{ padding: '10px', fontSize: '1em', borderRadius: '5px', border: '1px solid #007A33' }} 
                  disabled={loading}
                  required
                />
                {isRegister && (
                  <>
                    <input 
                      type="password" 
                      placeholder="Confirmar Contraseña" 
                      value={confirmPassword} 
                      onChange={(e) => setConfirmPassword(e.target.value)} 
                      style={{ padding: '10px', fontSize: '1em', borderRadius: '5px', border: '1px solid #007A33' }} 
                      disabled={loading}
                      required
                    />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', textAlign: 'left', marginTop: '5px' }}>
                      <input 
                        type="checkbox" 
                        id="terms"
                        checked={acceptedTerms} 
                        onChange={(e) => setAcceptedTerms(e.target.checked)} 
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        disabled={loading}
                      />
                      <label htmlFor="terms" style={{ fontSize: '0.85em', color: '#555', cursor: 'pointer' }}>
                        Acepto los <span style={{ color: '#007A33', textDecoration: 'underline' }}>Términos de Servicio</span>
                      </label>
                    </div>
                  </>
                )}
                
                {/* BOTÓN PRINCIPAL CON SPINNER INTEGRADO */}
                <button 
                  type="submit" 
                  disabled={loading}
                  style={{ 
                    padding: '10px', 
                    fontSize: '1.1em', 
                    backgroundColor: '#007A33', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '5px', 
                    cursor: 'pointer', 
                    fontWeight: 'bold', 
                    marginTop: '5px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {loading ? (
                    <>
                      <div className="spinner"></div>
                      {isRegister ? 'Procesando Registro...' : 'Iniciando Sesión...'}
                    </>
                  ) : (
                    isRegister ? 'Registrarse' : 'Ingresar'
                  )}
                </button>
              </form>
            ) : (
              // FORMULARIO DINÁMICO DE VALIDACIÓN OTP
              <form onSubmit={handleOtpSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                <p style={{ fontSize: '0.95em', color: '#555', margin: '0' }}>
                  Introduce el token de 6 dígitos enviado a: <br/><strong>{emailToVerify}</strong>
                </p>
                <input 
                  type="text" 
                  maxLength="6"
                  placeholder="000000" 
                  value={otpToken} 
                  onChange={(e) => setOtpToken(e.target.value)} 
                  style={{ padding: '12px', fontSize: '1.5em', letterSpacing: '6px', textAlign: 'center', borderRadius: '5px', border: '2px dashed #007A33', fontWeight: 'bold', color: '#007A33' }} 
                  disabled={loading}
                  required
                />

                {/* BOTÓN OTP CON SPINNER INTEGRADO */}
                <button 
                  type="submit" 
                  disabled={loading}
                  style={{ 
                    padding: '10px', 
                    fontSize: '1.1em', 
                    backgroundColor: '#007A33', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '5px', 
                    cursor: 'pointer', 
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {loading ? (
                    <>
                      <div className="spinner"></div>
                      Verificando Código...
                    </>
                  ) : (
                    'Verificar Código'
                  )}
                </button>
                <button 
                  type="button" 
                  disabled={loading}
                  onClick={() => { setIsNotVerified(false); setError(''); setSuccess(''); }} 
                  style={{ background: 'none', border: 'none', color: '#6c757d', cursor: 'pointer', fontSize: '0.9em', textDecoration: 'underline' }}
                >
                  Volver al Login
                </button>
              </form>
            )}
            
            <div style={{ marginTop: '15px', borderTop: '1px solid #eee', paddingTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <button 
                onClick={handleToggleMode} 
                disabled={loading}
                style={{ background: 'none', border: 'none', color: '#6c757d', cursor: 'pointer', fontSize: '0.95em', textDecoration: 'underline' }}
              >
                {isRegister ? '¿Ya tienes cuenta? Inicia Sesión' : '¿No tienes cuenta? Regístrate'}
              </button>
              
              {!isRegister && !isNotVerified && (
                <button 
                  onClick={() => navigate('/forgot-password')}
                  disabled={loading}
                  style={{ background: 'none', border: 'none', color: '#007A33', cursor: 'pointer', fontSize: '0.9em', fontWeight: 'bold' }}
                >
                  ¿Olvidaste tu contraseña?
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;